<template>
<m-pageframe
    title="{organization-label}: Lightbox Submissions"
>
<v-container
    fluid
    style="padding-left: 15px; padding-right: 15px;"
>
    <v-text-field
        autocomplete="off"
        label="Search submissions by name, email, phone, or lightbox"
        prepend-icon="mdi-magnify"
        v-model="full_text"
        v-on:input="update"
        clearable
        style="margin-bottom: 20px;"
    >
        <v-menu
            bottom
            offset-y
            slot="append-outer"
        >
            <template v-slot:activator="{ on }">
                <v-icon
                    v-on="on"
                >
                    mdi-dots-vertical
                </v-icon>
            </template>
    
            <v-list>
                <v-list-item
                    v-on:click="onClickExport()"
                >
                    <v-list-item-title>Export to Excel...</v-list-item-title>
                </v-list-item>

                <m-restricted-menu-list-item
                    v-on:click="onClickAddDummySubmissions()"
                    v-if="this.$rc.$state.isAuthActorSystemAdmin() && isDemoOrganization"
                    text="Add dummy submissions..."
                />


            </v-list>
        </v-menu>
    </v-text-field>

    <!-- Selected Users Summary - Only visible when users are selected -->
    <v-card
        v-if="hasSelectedSubmissions && (this.$rc.$state.isAuthActorSystemAdmin() || this.$rc.$state.isOrganizationAdmin())"
        class="mb-6"
        outlined
    >
        <v-card-title class="pb-2">
            <v-icon class="mr-2" color="primary">mdi-account-multiple</v-icon>
            Selected Users
            <v-spacer/>
            <v-chip
                :color="selected_submissions.length > 0 ? 'primary' : 'grey lighten-2'"
                :text-color="selected_submissions.length > 0 ? 'white' : 'grey darken-1'"
                small
            >
                {{selected_submissions.length}} selected
            </v-chip>
        </v-card-title>

        <v-card-text class="pt-0">
            <div v-if="selected_submissions.length === 0" class="text--secondary">
                Select users below to complete double opt-in
            </div>
            <div v-else>
                <v-chip
                    v-for="(item, index) in selected_submissions"
                    :key="'su_'+index"
                    class="mr-2 mb-2"
                    close
                    color="primary"
                    outlined
                    v-on:click:close="onClickRemoveSelection(item)"
                >
                    {{getSubmissionDisplayName(item)}}
                </v-chip>
            </div>
        </v-card-text>

        <v-card-actions v-if="(this.$rc.$state.isAuthActorSystemAdmin() || this.$rc.$state.isOrganizationAdmin())">
            <v-spacer/>
            <v-btn
                text
                color="error"
                v-on:click="clearAllSelections"
                :disabled="selected_submissions.length === 0"
                :style="{ visibility: selected_submissions.length > 0 ? 'visible' : 'hidden' }"
            >
                Clear All
            </v-btn>
            <v-btn
                color="primary"
                v-on:click="onClickCompleteDoubleOptIn"
                :disabled="!hasPendingSubmissions"
            >
                <v-icon left>mdi-check-circle</v-icon>
                Complete Double Opt-In
            </v-btn>
        </v-card-actions>
    </v-card>



    <!-- Enhanced Submissions Table -->
    <v-data-table
        v-if="results != null"
        :headers="headers"
        :items="results"
        :items-per-page="50"
        class="enhanced-submissions-table elevation-1"
        :hide-default-footer="false"
        disable-sort
        :no-data-text="no_data_message"
        :footer-props="{
            itemsPerPageText: 'Submissions per page:',
            itemsPerPageOptions: [25, 50, 100],
        }"
        v-model="selected_submissions"
        item-key="lightbox_submission_id"
        show-select
    >
        <template  v-slot:header.data-table-select>
            Select
        </template>
    
        <template v-slot:item.time_create_date="{ item }">
            {{$rc.pretty(item.time_create,'date')}}
        </template>

        <template v-slot:item.time_create_time="{ item }">
            {{$rc.pretty(item.time_create,'time')}}
        </template>

        <template v-slot:item.time_double_opt_in_date="{ item }">
            {{$rc.pretty(item.time_double_opt_in,'date')}}
        </template>

        <template v-slot:item.time_double_opt_in_time="{ item }">
            {{$rc.pretty(item.time_double_opt_in,'time')}}
        </template>

         <template v-slot:item.endpoint="{ item }">
            {{$rc.pretty(item.endpoint,'endpoint_no_parens')}}
        </template>

        <template v-slot:item.is_test_submission="{ item }">
            <v-chip
                small
                :color="item.is_test_submission ? 'orange' : 'green'"
                :text-color="item.is_test_submission ? 'white' : 'white'"
                class="status-chip"
            >
                {{ item.is_test_submission ? 'Test' : 'Live' }}
            </v-chip>
        </template>

        <template v-slot:item.state="{ item }">
            <v-chip
                small
                :color="getStateColor(item.state)"
                dark
                class="status-chip"
            >
                {{ getStateDisplayText(item.state) }}
            </v-chip>
        </template>
    
    </v-data-table>

    <!-- Footnote for State column -->
    <div class="mt-3 text-caption text--secondary">
        <strong>*</strong> State -- The outcome of the double opt-in process from this lightbox submission -- later changes in consent will not be reflected
    </div>

</v-container>

<!-- Bulk Opt-In Confirmation Modal -->
<v-dialog
    v-model="bulk_opt_in_dialog"
    max-width="800"
>
    <v-card>
        <v-card-title>Complete Double Opt-In</v-card-title>

        <v-card-text>
            <p><strong>You are about to manually complete double opt-in for {{bulk_opt_in_submission_count}} pending lightbox submission(s).</strong></p>

            <p>This action will:</p>
            <ul>
                <li>Register consent for the selected phone numbers</li>
                <li>Complete the lightbox submission process</li>
                <li>Log your action for audit purposes</li>
                <li>Allow these contacts to receive marketing messages</li>
            </ul>

            <p><strong>Selected submissions:</strong></p>
            <div v-html="bulk_opt_in_submission_details" class="mb-4"></div>

            <p><em>This action cannot be undone. Only proceed if you have proper authorization to grant consent on behalf of these contacts.</em></p>

            <m-legal-agreement
                type="lightbox-bulk-opt-in-attestation"
                :organization_id="$route.params.organization_id"
                v-model="consent_acknowledgement"
                ref="bulk_opt_in_attestation"
                class="mt-4"
            />
        </v-card-text>

        <v-card-actions>
            <v-spacer></v-spacer>

            <v-btn
                text
                @click="onCancelBulkOptIn"
            >
                Cancel
            </v-btn>

            <v-btn
                color="primary"
                @click="onConfirmBulkOptIn"
                :disabled="!consent_acknowledgement"
            >
                <v-icon left>mdi-check-circle</v-icon>
                Complete Double Opt-In
            </v-btn>
        </v-card-actions>
    </v-card>
</v-dialog>

</m-pageframe>
</template>

<script>
const _ = require('lodash');
export default {
    data() {
        return {
            results: [],
            selected_submissions: [],
            organization_lightboxes: null,
            isDemoOrganization: false,

            headers: [
                { text: 'Type', value: 'is_test_submission'},
                { text: 'Lightbox', value: 'lightbox_label'},
                { text: 'Date', value: 'time_create_date'},
                { text: 'Time', value: 'time_create_time'},

                { text: 'First Name', value: 'first_name'},
                { text: 'Last Name', value: 'last_name'},
                { text: 'Email', value: 'email'},
                { text: 'Phone', value: 'endpoint'},
                { text: 'Birthday', value: 'birthday'},


                { text: 'State *', value: 'state'},
                { text: 'Double Opt In Date', value: 'time_double_opt_in_date'},
                { text: 'Double Opt In Time', value: 'time_double_opt_in_time'},
            ],

            full_text: null,

            // Bulk opt-in modal state
            bulk_opt_in_dialog: false,
            bulk_opt_in_submission_ids: [],
            bulk_opt_in_submission_count: 0,
            bulk_opt_in_submission_details: '',
            consent_acknowledgement: false,
        }
    },

    computed: {
        hasSelectedSubmissions() {
            return this.selected_submissions && this.selected_submissions.length > 0;
        },
        hasPendingSubmissions() {
            if (!this.selected_submissions || this.selected_submissions.length === 0) {
                return false;
            }
            return this.selected_submissions.some(submission =>
                submission.state === 'pending-double-opt-in'
            );
        },
        no_data_message() {
            if (this.full_text && this.full_text.trim()) {
                return `No submissions found matching "${this.full_text}"`;
            }
            return "No submissions available";
        }
    },

    mounted () {
        this.doUpdate();
        this.loadOrganizationInfo();
    },

    methods: {
        update: _.debounce(function(){
            this.doUpdate();
        }, 751),
        
        doUpdate()
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'LIST: /api/v1/lightbox/submissions',

                'params' : {
                        'organization_id': this.$route.params.organization_id,
                        'full_text': this.full_text,
                    },

                'on_success': (data) => {
                        this.results = data.results;
                    },

                'on_error': 'Unable to list lightbox submissions',
            });
        },


        
        onClickExport()
        {
            this.$rc.excelExport(
                    'EXPORT: /api/v1/lightbox/submissions',
                    {
                        'organization_id': this.$route.params.organization_id,
                    }
                );
        },

        loadOrganizationInfo()
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET:/api/v1/lightbox/organization/:organization_id',

                'params' : {
                        'organization_id': this.$route.params.organization_id,
                    },

                'on_success': (data) => {
                        this.organization_lightboxes = data;
                        // Check if this is a demo organization by looking at the organization account
                        this.checkIfDemoOrganization();
                    },

                'on_error': 'Unable to load organization lightbox data',
            });
        },

        checkIfDemoOrganization()
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET: /api/v1/organizations/organization-account/:organization_id',

                'params' : {
                        'organization_id': this.$route.params.organization_id,
                    },

                'on_success': (data) => {
                        this.isDemoOrganization = data.is_demo_organization;
                    },

                'on_error': 'Unable to check organization status',
            });
        },

        onClickAddDummySubmissions()
        {
            // Ensure organization lightboxes are loaded
            if (!this.organization_lightboxes) {
                this.$rc.alert('Error loading lightbox data, please try again in a moment');
                return;
            }

            if (!this.organization_lightboxes.settings || this.organization_lightboxes.settings.length === 0) {
                this.$rc.alert('No lightboxes available for this organization');
                return;
            }

            // Create lightbox options for selection
            let lightbox_options = this.organization_lightboxes.settings.map(lightbox => ({
                text: lightbox.label,
                value: lightbox.lightbox_id
            }));



            this.$rc.promptSelect({
                'title': 'Select Lightbox',
                'label': 'Choose a lightbox to add dummy submissions to:',
                'items': lightbox_options,
                'after_prompt': (lightbox_id) => {
                    if (!lightbox_id) return;

                    this.promptForSubmissionCount(lightbox_id);
                }
            });
        },

        promptForSubmissionCount(lightbox_id)
        {
            this.$rc.prompt({
                'title': 'Number of submissions',
                'label': 'Number of dummy submissions to create',
                'value': '10',
                'after_prompt': (value) => {
                    let count = parseInt(value);

                    if (Number.isNaN(count) || count <= 0) {
                        this.$rc.alert('Please enter a valid number');
                        return;
                    }

                    this.createDummySubmissions(lightbox_id, count);
                }
            });
        },

        createDummySubmissions(lightbox_id, count)
        {
            const self = this;

            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/lightbox/add-dummy-submissions',

                'params': {
                    'lightbox_id': lightbox_id,
                    'count': count,
                },

                'on_success': function(data) {
                    self.$rc.snackbar(data.message);
                    self.doUpdate();
                },

                'on_error': 'Unable to create dummy submissions',
            });
        },

        onClickCompleteDoubleOptIn()
        {
            if (!this.selected_submissions || this.selected_submissions.length === 0) {
                this.$rc.alert('Please select submissions to complete double opt-in for');
                return;
            }

            // Filter to only pending submissions
            let pending_submissions = this.selected_submissions.filter(submission =>
                submission.state === 'pending-double-opt-in'
            );

            if (pending_submissions.length === 0) {
                this.$rc.alert('No pending double opt-in submissions selected');
                return;
            }

            let submission_ids = pending_submissions.map(submission => submission.lightbox_submission_id);

            // Create detailed confirmation message
            let submission_details = pending_submissions.map(sub => {
                let name = [sub.first_name, sub.last_name].filter(n => n).join(' ') || 'No Name';
                let phone = sub.endpoint || 'No phone';
                return `• ${name} (${phone})`;
            }).join('<br/>');

            // Show consolidated modal with explanation and legal agreement
            this.showBulkOptInModal(submission_ids, pending_submissions.length, submission_details);
        },

        showBulkOptInModal(submission_ids, submission_count, submission_details)
        {
            this.bulk_opt_in_submission_ids = submission_ids;
            this.bulk_opt_in_submission_count = submission_count;
            this.bulk_opt_in_submission_details = submission_details;
            this.consent_acknowledgement = false;
            this.bulk_opt_in_dialog = true;
        },

        onCancelBulkOptIn()
        {
            this.bulk_opt_in_dialog = false;
            this.consent_acknowledgement = false;
        },

        onConfirmBulkOptIn()
        {
            if (!this.consent_acknowledgement) {
                this.$rc.alert('Please agree to the attestation to proceed');
                return;
            }

            const specimen = this.$refs['bulk_opt_in_attestation'].getSpecimen();
            this.bulk_opt_in_dialog = false;
            this.completeDoubleOptInBatch(this.bulk_opt_in_submission_ids, specimen);
        },

        completeDoubleOptInBatch(submission_ids, specimen)
        {
            const self = this;

            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/lightbox/complete-double-opt-in-batch',

                'params': {
                    'organization_id': this.$route.params.organization_id,
                    'submission_ids': submission_ids,
                    'specimen': specimen,
                },

                'on_success': function(data) {
                    self.$rc.snackbar(data.message);
                    self.selected_submissions = [];
                    self.consent_acknowledgement = false;
                    self.doUpdate();
                },

                'on_error': 'Unable to complete double opt-in',
            });
        },

        getSubmissionDisplayName(submission) {
            let name = [submission.first_name, submission.last_name].filter(n => n).join(' ') || 'No Name';
            let phone = submission.endpoint || 'No phone';
            return `${name} (${phone})`;
        },

        onClickRemoveSelection(submission) {
            this.selected_submissions = this.selected_submissions.filter(s =>
                s.lightbox_submission_id !== submission.lightbox_submission_id
            );
        },

        clearAllSelections() {
            this.selected_submissions = [];
        },

        getStateColor(state) {
            switch (state) {
                case 'pending-double-opt-in':
                    return 'orange';
                case 'complete':
                    return 'green';
                case 'error':
                    return 'red';
                case 'opted-out':
                    return 'grey';
                default:
                    return 'grey';
            }
        },

        getStateDisplayText(state) {
            //Use pretty function to convert enum to human readable text
            return this.$rc.pretty(state, 'enum_lightbox_submission_state');
        }
    },
}
</script>

<style lang="scss" scoped>
.enhanced-submissions-table {
    ::v-deep .v-data-table__wrapper {
        border-radius: 8px;
    }

    ::v-deep tbody tr {
        transition: all 0.2s ease-in-out;

        &:hover {
            background-color: rgba(var(--v-primary-base), 0.04) !important;
        }

        &.v-data-table__selected {
            background-color: rgba(var(--v-primary-base), 0.08) !important;

            &:hover {
                background-color: rgba(var(--v-primary-base), 0.12) !important;
            }
        }
    }

    ::v-deep th {
        font-weight: 600 !important;
        color: rgba(0, 0, 0, 0.87) !important;
        background-color: #fafafa !important;
    }

    ::v-deep td {
        padding: 16px !important;
    }
}

.status-chip {
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}
</style>